import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { backendURL } from "../config";
import {
    Shield, ArrowLeft, Check, Copy, Sparkles, Terminal,
    Play, AlertTriangle, ShieldCheck, CheckCircle2, Clock,
    Sliders, FileCode, CheckCircle, RefreshCw, X, ChevronRight,
    SlidersHorizontal, Bot, Zap, History, FileText
} from "lucide-react";

export default function RuleRecommendations() {
    const { ruleId = "rule-md-01" } = useParams();
    const navigate = useNavigate();

    const [recData, setRecData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("recommendations"); // details | recommendations | history
    const [enabled, setEnabled] = useState(true);
    const [copied, setCopied] = useState(false);
    const [applied, setApplied] = useState(false);
    const [toast, setToast] = useState(null);

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchRecommendations();
    }, [ruleId]);

    const fetchRecommendations = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/detect/rules/${ruleId}/recommendations`, { headers });
            setRecData(res.data);
            setEnabled(res.data.enabled !== false);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to load recommendations:", err);
        }
        setLoading(false);
    };

    const handleToggleRule = async () => {
        const nextState = !enabled;
        setEnabled(nextState);
        try {
            await axios.post(`${backendURL}/api/detect/rules/${ruleId}/toggle`, { enabled: nextState }, { headers });
            setToast(`Rule ${nextState ? "Enabled" : "Disabled"}`);
            setTimeout(() => setToast(null), 2500);
        } catch (err) {
            console.error("Failed to toggle rule:", err);
        }
    };

    const handleCopyQuery = () => {
        if (!recData?.tuningRecommendation?.scopedQuery) return;
        navigator.clipboard.writeText(recData.tuningRecommendation.scopedQuery);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleApplyTuning = async () => {
        try {
            const res = await axios.post(
                `${backendURL}/api/detect/rules/${ruleId}/apply-tuning`,
                { tunedQuery: recData?.tuningRecommendation?.scopedQuery },
                { headers }
            );
            setApplied(true);
            setToast(res.data.message || "QueryTel Scoped Tuning applied!");
            setTimeout(() => setToast(null), 4000);
        } catch (err) {
            console.error("Failed to apply tuning:", err);
        }
    };

    const determinations = recData?.determinations || { benign: 0, suspicious: 0, malicious: 0 };
    const totalDet = (determinations.benign + determinations.suspicious + determinations.malicious) || 1;
    const benignPct = (determinations.benign / totalDet) * 100;
    const suspiciousPct = (determinations.suspicious / totalDet) * 100;
    const maliciousPct = (determinations.malicious / totalDet) * 100;

    return (
        <div className="w-full space-y-6 pb-12">
            {/* ─── Toast Feedback ─── */}
            {toast && (
                <div className="fixed bottom-6 right-6 z-50 bg-emerald-600 text-white px-4 py-2.5 rounded-xl shadow-2xl text-[12px] font-bold flex items-center gap-2 animate-in fade-in slide-in-from-bottom-4 duration-200">
                    <Check className="w-4 h-4" />
                    {toast}
                </div>
            )}

            {/* ─── Breadcrumb ─── */}
            <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                <span>04</span>
                <span>/</span>
                <Link to="/detection-rules" className="hover:text-blue-400 transition-colors">Detection Rules</Link>
                <span>/</span>
                <span className="text-slate-400">Recommendations</span>
            </div>

            {/* ─── Main Hero Layout (2 Columns) ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                
                {/* ═══ LEFT HERO DESCRIPTION (5 cols) ═══ */}
                <div className="lg:col-span-5 space-y-6">
                    <div>
                        <h1 className="text-[28px] lg:text-[34px] font-black text-white tracking-tight leading-tight">
                            Find the noise. Tune it down.
                        </h1>
                        <p className="text-[13.5px] text-slate-300 mt-3 leading-relaxed">
                            QueryTel watches how every rule performs and flags the ones generating false positives at scale. For each one it explains why, quantifies the cost in analyst time, and recommends a precise change <strong>scoped to confirmed threat signals</strong>, so real detections rise and the noise falls.
                        </p>
                    </div>

                    <div className="space-y-2.5 pt-2">
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>False positive rate measured for every rule.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>A specific, scoped tuning recommendation, not a vague score.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>You stay in control, with humans on the loop.</span>
                        </div>
                    </div>

                    <div className="pt-2">
                        <Link
                            to="/detection-rules"
                            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#111827] hover:bg-[#1E293B] border border-white/[0.08] text-slate-300 rounded-xl text-[12px] font-semibold transition-all"
                        >
                            <ArrowLeft className="w-3.5 h-3.5" />
                            Back to Detection Rules
                        </Link>
                    </div>
                </div>

                {/* ═══ RIGHT WORKBENCH: Recommendations Canvas (7 cols) ═══ */}
                <div className="lg:col-span-7 bg-[#111827] border border-white/[0.06] rounded-2xl p-6 shadow-2xl space-y-6">
                    
                    {/* Header: Rule Title + Enable Switch */}
                    <div className="flex items-center justify-between border-b border-white/[0.06] pb-4">
                        <div>
                            <span className="text-[11px] font-mono uppercase text-slate-400 block mb-0.5">
                                {recData?.source || "Microsoft Defender"}
                            </span>
                            <h2 className="text-[20px] font-black text-white tracking-tight">
                                {recData?.name || "User Reported Phishing"}
                            </h2>
                        </div>

                        {/* Enable/Disable Toggle */}
                        <div className="flex items-center gap-2.5">
                            <span className="text-[12px] font-medium text-slate-400">
                                {enabled ? "Enabled" : "Disabled"}
                            </span>
                            <button
                                onClick={handleToggleRule}
                                className={`w-11 h-6 rounded-full transition-colors relative p-0.5 focus:outline-none ${
                                    enabled ? "bg-blue-600" : "bg-slate-700"
                                }`}
                            >
                                <div
                                    className={`w-5 h-5 rounded-full bg-white transition-transform ${
                                        enabled ? "translate-x-5" : "translate-x-0"
                                    }`}
                                />
                            </button>
                        </div>
                    </div>

                    {/* 3 Tabs */}
                    <div className="flex items-center gap-6 border-b border-white/[0.06] pb-2 text-[13px] font-bold">
                        <button
                            onClick={() => setActiveTab("details")}
                            className={`pb-2 transition-all relative ${
                                activeTab === "details" ? "text-blue-400" : "text-slate-400 hover:text-slate-200"
                            }`}
                        >
                            Rule Details
                            {activeTab === "details" && (
                                <span className="absolute bottom-[-9px] left-0 right-0 h-0.5 bg-blue-500 rounded-full" />
                            )}
                        </button>

                        <button
                            onClick={() => setActiveTab("recommendations")}
                            className={`pb-2 transition-all relative ${
                                activeTab === "recommendations" ? "text-blue-400" : "text-slate-400 hover:text-slate-200"
                            }`}
                        >
                            Recommendations
                            {activeTab === "recommendations" && (
                                <span className="absolute bottom-[-9px] left-0 right-0 h-0.5 bg-blue-500 rounded-full" />
                            )}
                        </button>

                        <button
                            onClick={() => setActiveTab("history")}
                            className={`pb-2 transition-all relative ${
                                activeTab === "history" ? "text-blue-400" : "text-slate-400 hover:text-slate-200"
                            }`}
                        >
                            History
                            {activeTab === "history" && (
                                <span className="absolute bottom-[-9px] left-0 right-0 h-0.5 bg-blue-500 rounded-full" />
                            )}
                        </button>
                    </div>

                    {/* ═══ TAB CONTENT: RECOMMENDATIONS ═══ */}
                    {activeTab === "recommendations" && (
                        <div className="space-y-6 animate-in fade-in duration-150">
                            
                            {/* 4 KPI Cards */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <div className="p-3.5 bg-black/30 border border-white/[0.06] rounded-xl space-y-1">
                                    <span className="text-[20px] font-extrabold text-white font-mono block">
                                        {recData?.metrics?.detections || 0}
                                    </span>
                                    <span className="text-[11px] text-slate-400 block">Detections</span>
                                </div>

                                <div className="p-3.5 bg-black/30 border border-white/[0.06] rounded-xl space-y-1">
                                    <span className="text-[20px] font-extrabold text-white font-mono block">
                                        {recData?.metrics?.investigations || 0}
                                    </span>
                                    <span className="text-[11px] text-slate-400 block">Investigations</span>
                                </div>

                                <div className="p-3.5 bg-black/30 border border-white/[0.06] rounded-xl space-y-1">
                                    <span className="text-[20px] font-extrabold text-amber-400 font-mono block">
                                        {recData?.metrics?.fpRate ?? 0}%
                                    </span>
                                    <span className="text-[11px] text-slate-400 block">False Positive Rate</span>
                                </div>

                                <div className="p-3.5 bg-black/30 border border-white/[0.06] rounded-xl space-y-1">
                                    <span className="text-[20px] font-extrabold text-white font-mono block">
                                        {recData?.metrics?.dailyAverage ?? 0}
                                    </span>
                                    <span className="text-[11px] text-slate-400 block">Daily Average</span>
                                </div>
                            </div>

                            {/* Investigation Determinations Bar */}
                            <div className="space-y-2">
                                <span className="text-[12px] font-bold text-white block">Investigation Determinations</span>
                                
                                {/* Stacked Progress Bar */}
                                <div className="w-full h-3.5 rounded-full overflow-hidden flex bg-black/60 border border-white/[0.06]">
                                    <div style={{ width: `${benignPct}%` }} className="bg-emerald-500 h-full" title={`Benign: ${determinations.benign}`} />
                                    <div style={{ width: `${suspiciousPct}%` }} className="bg-amber-500 h-full" title={`Suspicious: ${determinations.suspicious}`} />
                                    <div style={{ width: `${maliciousPct}%` }} className="bg-rose-500 h-full" title={`Malicious: ${determinations.malicious}`} />
                                </div>

                                {/* Legend Labels */}
                                <div className="flex items-center gap-4 text-[11.5px] pt-1">
                                    <span className="flex items-center gap-1.5 text-slate-300">
                                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                                        Benign <strong className="text-white font-mono">{determinations.benign}</strong>
                                    </span>
                                    <span className="flex items-center gap-1.5 text-slate-300">
                                        <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                                        Suspicious <strong className="text-white font-mono">{determinations.suspicious}</strong>
                                    </span>
                                    <span className="flex items-center gap-1.5 text-slate-300">
                                        <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
                                        Malicious <strong className="text-white font-mono">{determinations.malicious}</strong>
                                    </span>
                                </div>
                            </div>

                            {/* Efficacy Score Card */}
                            <div className="space-y-2">
                                <span className="text-[12px] font-bold text-white block">Efficacy Score</span>
                                <div className="p-4 bg-black/30 border border-white/[0.06] rounded-xl grid grid-cols-2 gap-4 text-[12px]">
                                    <div>
                                        <span className="text-slate-500 block text-[11px]">Score summary</span>
                                        <span className="font-bold text-amber-400 text-[13px]">{recData?.efficacy?.scoreSummary || "Low"}</span>
                                    </div>
                                    <div>
                                        <span className="text-slate-500 block text-[11px]">Source severity</span>
                                        <span className="font-bold text-slate-200 text-[13px]">{recData?.efficacy?.sourceSeverity || "Medium"}</span>
                                    </div>
                                    <div>
                                        <span className="text-slate-500 block text-[11px]">False positive rate</span>
                                        <span className="font-semibold text-slate-300 text-[12px]">{recData?.efficacy?.falsePositiveRate || "317/467 Not escalated, 68% FP"}</span>
                                    </div>
                                    <div>
                                        <span className="text-slate-500 block text-[11px]">Volume</span>
                                        <span className="font-bold text-slate-200 text-[13px]">{recData?.efficacy?.volume || "High Volume"}</span>
                                    </div>
                                </div>
                            </div>

                            {/* QueryTel Tuning Recommendations */}
                            <div className="space-y-2">
                                <span className="text-[12px] font-bold text-white block">Tuning Recommendations</span>
                                <div className="p-5 bg-gradient-to-br from-[#0F172A] to-[#1E1B4B]/30 border border-indigo-500/20 rounded-2xl space-y-4 shadow-lg">
                                    
                                    {/* Recommendation Header */}
                                    <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded bg-black flex items-center justify-center font-bold text-[10px] text-white">
                                            QueryTel
                                        </div>
                                        <h4 className="text-[13.5px] font-extrabold text-white">QueryTel Tuning Recommendation</h4>
                                    </div>

                                    {/* Narrative Explanation */}
                                    <p className="text-[12.5px] text-slate-300 leading-relaxed">
                                        {recData?.tuningRecommendation?.explanation}
                                    </p>

                                    {/* Scoped Logic Section */}
                                    <div className="space-y-2">
                                        <span className="text-[11.5px] font-bold text-indigo-300 block">
                                            {recData?.tuningRecommendation?.scopeTitle}
                                        </span>

                                        <div className="relative group">
                                            <pre className="p-3.5 bg-black/70 border border-white/[0.08] rounded-xl text-[11.5px] font-mono text-emerald-400 overflow-x-auto custom-scrollbar leading-relaxed">
                                                {recData?.tuningRecommendation?.scopedQuery}
                                            </pre>
                                            <button
                                                onClick={handleCopyQuery}
                                                className="absolute top-2.5 right-2.5 p-1.5 bg-white/[0.08] hover:bg-white/[0.15] text-slate-300 rounded-lg text-[10px] transition-colors flex items-center gap-1"
                                                title="Copy Query"
                                            >
                                                {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                                                {copied ? "Copied" : "Copy"}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Apply & Actions Bar */}
                                    <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t border-white/[0.06]">
                                        <span className="text-[11px] font-mono text-emerald-400">
                                            ✓ {recData?.tuningRecommendation?.expectedNoiseReduction}
                                        </span>

                                        <button
                                            onClick={handleApplyTuning}
                                            disabled={applied}
                                            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl text-[12px] font-bold shadow-lg shadow-blue-600/20 transition-all flex items-center gap-1.5 justify-center"
                                        >
                                            {applied ? <Check className="w-4 h-4 text-emerald-300" /> : <Zap className="w-4 h-4" />}
                                            {applied ? "Tuning Applied!" : "Apply QueryTel Tuning"}
                                        </button>
                                    </div>

                                </div>
                            </div>

                        </div>
                    )}

                    {/* ═══ TAB CONTENT: RULE DETAILS ═══ */}
                    {activeTab === "details" && (
                        <div className="space-y-4 text-[12.5px] text-slate-300 animate-in fade-in duration-150">
                            <div className="p-4 bg-black/30 border border-white/[0.06] rounded-xl space-y-2">
                                <span className="text-[11px] font-bold uppercase text-slate-400 block">Original Query Logic</span>
                                <pre className="p-3 bg-black/60 rounded-lg font-mono text-[11px] text-slate-200 overflow-x-auto">
                                    {recData?.tuningRecommendation?.originalQuery}
                                </pre>
                            </div>
                            <div className="p-4 bg-black/30 border border-white/[0.06] rounded-xl space-y-1">
                                <span className="text-[11px] font-bold uppercase text-slate-400 block">Connected Connector</span>
                                <p className="text-white font-medium">{recData?.source}</p>
                            </div>
                        </div>
                    )}

                    {/* ═══ TAB CONTENT: HISTORY ═══ */}
                    {activeTab === "history" && (
                        <div className="space-y-3 animate-in fade-in duration-150 text-[12px]">
                            {(recData?.history || []).map((item, idx) => (
                                <div key={idx} className="p-3.5 bg-black/30 border border-white/[0.04] rounded-xl flex items-center justify-between">
                                    <div className="space-y-0.5">
                                        <p className="text-white font-medium">{item.action}</p>
                                        <span className="text-[10px] text-slate-500 font-mono">By: {item.user}</span>
                                    </div>
                                    <span className="text-[10.5px] font-mono text-slate-400">{item.date}</span>
                                </div>
                            ))}
                        </div>
                    )}

                </div>

            </div>
        </div>
    );
}
